import { useEffect, useMemo } from 'react'
import { Logger } from '@/components/Logger'
import { useGetUserSelf } from '@/hooks/v1'
import { getUserCouponPaymentsQueryKey } from '@/hooks/v1/hooks/useGetUserCouponPayments'
import type {
  GetUserCouponPaymentsQuery,
  GetUserCouponPaymentsStatus200,
} from '@/hooks/v1/types/GetUserCouponPayments'
import type { UserCouponPayment } from '@/hooks/v1/types/UserCouponPayment'
import type { UserCouponPaymentAssetSummary } from '@/hooks/v1/types/UserCouponPaymentAssetSummary'
import { queryClient } from '@/services/api'
import { subscribeRealtime } from '@/services/api/realtimeSubscription'
import { isWebSocketErrorMessage } from './useWebSocketStatus'

type UseUserCouponPaymentsSubscriptionOptions = {
  enabled?: boolean
}
type StreamUserCouponPaymentsEntry = { Val?: UserCouponPayment }

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object')

const hasValWrapper = (value: unknown): value is StreamUserCouponPaymentsEntry =>
  isRecord(value) && 'Val' in value

const isUserCouponPayment = (value: unknown): value is UserCouponPayment =>
  isRecord(value) &&
  ('coupon_payment_id' in value ||
    'asset_id' in value ||
    'position_id' in value ||
    'user_id' in value)

const isUserCouponPaymentSummary = (value: unknown): value is UserCouponPaymentAssetSummary =>
  isRecord(value) && 'asset_id' in value

const extractFromEntry = (item: unknown): UserCouponPayment[] => {
  if (hasValWrapper(item) && isUserCouponPayment(item.Val)) {
    return [item.Val]
  }

  if (isUserCouponPayment(item)) {
    return [item]
  }

  return []
}

const extractCouponPayments = (message: unknown): UserCouponPayment[] => {
  if (Array.isArray(message)) {
    return message.flatMap(extractFromEntry)
  }

  if (isRecord(message)) {
    if (Array.isArray(message.entries)) {
      return message.entries.flatMap(extractFromEntry)
    }

    return extractFromEntry(message)
  }

  return []
}

const extractSummaryByAsset = (message: unknown): UserCouponPaymentAssetSummary[] | null => {
  if (!isRecord(message) || !('summary_by_asset' in message)) {
    return null
  }

  const summaries = message.summary_by_asset
  if (!Array.isArray(summaries)) {
    return []
  }

  return summaries.filter(isUserCouponPaymentSummary)
}

const getCouponPaymentKey = (payment: UserCouponPayment): string | undefined => {
  if (payment.coupon_payment_id) {
    return payment.coupon_payment_id
  }

  const assetId = payment.asset_id
  const positionId = payment.position_id
  const seq = payment.seq

  if (assetId && positionId && typeof seq !== 'undefined') {
    return `${assetId}:${positionId}:${seq}`
  }

  if (assetId && positionId) {
    return `${assetId}:${positionId}`
  }

  return undefined
}

const mergeCouponPayments = (
  existing: UserCouponPayment[],
  updates: UserCouponPayment[],
  allowInsert: boolean,
  limit?: number,
): UserCouponPayment[] => {
  if (updates.length === 0) {
    return existing
  }

  const existingByKey = new Map<string, UserCouponPayment>()
  const existingOrder: string[] = []
  const existingNoKey: UserCouponPayment[] = []

  for (const payment of existing) {
    const key = getCouponPaymentKey(payment)
    if (key) {
      existingByKey.set(key, payment)
      existingOrder.push(key)
    } else {
      existingNoKey.push(payment)
    }
  }

  const newItems: UserCouponPayment[] = []
  let changed = false

  for (const update of updates) {
    const key = getCouponPaymentKey(update)
    if (key) {
      const previous = existingByKey.get(key)
      if (previous) {
        const hasDiff = Object.entries(update as Record<string, unknown>).some(
          ([entryKey, value]) => (previous as Record<string, unknown>)[entryKey] !== value,
        )

        if (hasDiff) {
          existingByKey.set(key, { ...previous, ...update })
          changed = true
        }
      } else if (allowInsert) {
        newItems.push(update)
        changed = true
      }
    } else if (allowInsert) {
      newItems.push(update)
      changed = true
    }
  }

  if (!changed) {
    return existing
  }

  const updatedExisting = existingOrder
    .map((key) => existingByKey.get(key))
    .filter(Boolean) as UserCouponPayment[]

  const merged = allowInsert
    ? [...newItems, ...updatedExisting, ...existingNoKey]
    : [...updatedExisting, ...existingNoKey]

  if (
    allowInsert &&
    typeof limit === 'number' &&
    Number.isFinite(limit) &&
    limit > 0 &&
    merged.length > limit
  ) {
    return merged.slice(0, limit)
  }

  return merged
}

const filterCouponPayments = (
  payments: UserCouponPayment[],
  params?: GetUserCouponPaymentsQuery,
): UserCouponPayment[] => {
  if (!params?.asset_id && !params?.position_id) {
    return payments
  }

  return payments.filter((payment) => {
    if (params.asset_id && payment.asset_id !== params.asset_id) {
      return false
    }
    if (params.position_id && payment.position_id !== params.position_id) {
      return false
    }
    return true
  })
}

const filterSummaryByAsset = (
  summaries: UserCouponPaymentAssetSummary[],
  params?: GetUserCouponPaymentsQuery,
): UserCouponPaymentAssetSummary[] => {
  if (!params?.asset_id) {
    return summaries
  }

  return summaries.filter((summary) => summary.asset_id === params.asset_id)
}

export const useUserCouponPaymentsSubscription = ({
  enabled = true,
}: UseUserCouponPaymentsSubscriptionOptions = {}) => {
  const { data: userSelfData } = useGetUserSelf()
  const userSelf = useMemo(() => userSelfData?.data ?? null, [userSelfData])
  const userId = useMemo(() => userSelf?.id ?? null, [userSelf])

  useEffect(() => {
    if (!enabled || !userId) return

    const unsubscribe = subscribeRealtime(
      {
        onMessage: (data) => {
          try {
            if (isWebSocketErrorMessage(data)) {
              Logger.error(
                'DEBUG WS COUPON PAYMENTS: Received error message, skipping setQueryData:',
                data,
              )
              return
            }

            if (
              !data ||
              (typeof data === 'object' && Object.keys(data).length === 0) ||
              data === ''
            ) {
              return
            }

            const updates = extractCouponPayments(data)
            const summaryUpdates = extractSummaryByAsset(data)
            const hasSummaryUpdates = summaryUpdates !== null

            if (!updates.length && !hasSummaryUpdates) {
              return
            }

            const baseQueryKey = getUserCouponPaymentsQueryKey({ path: { user_id: userId } })
            const cachedQueries = queryClient.getQueriesData({ queryKey: baseQueryKey })
            const queryKeys = cachedQueries.length
              ? cachedQueries.map(([queryKey]) => queryKey)
              : [baseQueryKey]

            queryKeys.forEach((queryKey) => {
              const params =
                Array.isArray(queryKey) && queryKey.length > 1
                  ? (queryKey[1] as GetUserCouponPaymentsQuery)
                  : undefined
              const allowInsert = !params?.page || params.page === 1
              const filteredUpdates = filterCouponPayments(updates, params)
              const filteredSummaries = hasSummaryUpdates
                ? filterSummaryByAsset(summaryUpdates ?? [], params)
                : undefined

              queryClient.setQueryData(
                queryKey,
                (previous: GetUserCouponPaymentsStatus200 | undefined) => {
                  const prevData = previous?.data ?? {}
                  const existingPayments = Array.isArray(prevData.coupon_payments)
                    ? prevData.coupon_payments
                    : []
                  const nextPayments =
                    filteredUpdates.length > 0
                      ? mergeCouponPayments(
                          existingPayments,
                          filteredUpdates,
                          allowInsert,
                          params?.limit,
                        )
                      : existingPayments

                  const nextSummary = hasSummaryUpdates
                    ? (filteredSummaries ?? [])
                    : prevData.summary_by_asset

                  if (
                    filteredUpdates.length === 0 &&
                    !hasSummaryUpdates &&
                    nextPayments === existingPayments
                  ) {
                    return previous
                  }

                  return {
                    ...(previous ?? {}),
                    data: {
                      ...prevData,
                      coupon_payments: nextPayments,
                      ...(hasSummaryUpdates ? { summary_by_asset: nextSummary } : {}),
                    },
                  }
                },
              )
            })
          } catch (error) {
            Logger.error('DEBUG WS COUPON PAYMENTS: Error parsing WebSocket message:', error)
          }
        },
        onOpen: () => {
          Logger.log(
            'DEBUG WS COUPON PAYMENTS: WebSocket connected successfully to user coupon payments stream',
          )
        },
        onError: (error) => {
          Logger.error('DEBUG WS COUPON PAYMENTS: WebSocket error:', error)
        },
        onClose: (event) => {
          Logger.log(
            'DEBUG WS COUPON PAYMENTS: WebSocket connection closed, code:',
            event.code,
            'wasClean:',
            event.wasClean,
          )
        },
      },
      {
        key: `coupon-payments:${userId}`,
        path: '/coupon-payments/byuser',
        subscribeData: { subscribe: [userId] },
        unsubscribeData: { unsubscribe: [userId] },
        mapNotification: (data) => {
          if (!data || typeof data !== 'object') return data
          const payload = data as {
            coupon_payments?: Record<string, unknown>
            summary_by_asset?: Record<string, unknown>
          }
          return {
            entries: Array.isArray(payload.coupon_payments?.[userId])
              ? payload.coupon_payments[userId]
              : [],
            summary_by_asset: Array.isArray(payload.summary_by_asset?.[userId])
              ? payload.summary_by_asset[userId]
              : [],
          }
        },
      },
    )

    return () => {
      unsubscribe()
    }
  }, [enabled, userId])
}
